import { motion } from "framer-motion";
import { PARTNERSHIP_TYPES } from "@/data/content";
import { FAQ_GROUPS } from "@/data/faqs";
import { PageHero, SectionHead, FaqList, BulletList } from "@/components/Blocks";
import { Cta } from "@/components/Cta";
import { Reveal, Stagger, itemVariants } from "@/components/motion";
import PartnershipForm from "@/components/forms/PartnershipForm";
import { useSeo } from "@/lib/seo";

const INTERESTS = [
  "Founders with deep domain knowledge of an important problem",
  "B2B software, developer tools, and AI-enabled products",
  "Ideas where strong product engineering is a real advantage",
  "Teams committed to the long road from validation to growth",
];
const WE_BRING = ["Product strategy and discovery", "Senior engineering and architecture", "UX/UI design and prototyping", "AI engineering experience", "Experience launching our own ventures"];
const YOU_BRING = ["Deep understanding of the problem and customers", "Commitment and time to lead the venture", "Access to early users and market insight", "Go-to-market ownership", "Transparency about goals and constraints"];
const EVAL = [
  ["Application", "You share the idea, problem, stage, and team through the form below."],
  ["Review", "We assess the problem, market, fit with our strengths, and team."],
  ["Conversation", "If there's potential, we meet to go deeper and ask hard questions — both ways."],
  ["Proposal", "If both sides want to proceed, we discuss a collaboration model and terms."],
];

export default function VenturePartnerships() {
  useSeo("Venture Partnerships", "Partner with SparkIdea Interactive to explore, validate, and co-build promising software ventures. Partnerships are selective.");
  return (
    <>
      <PageHero testId="vp-hero" eyebrow="Venture partnerships" title={["Let's build something", "worth building."]}
        intro="We selectively partner with founders who see an important problem and want a committed technology partner to help build the solution.">
        <Cta href="#apply" size="lg" testId="vp-hero-cta">Pitch Your Idea</Cta>
      </PageHero>
      <section className="border-t border-si-line py-20 sm:py-28">
        <div className="wrap grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5"><SectionHead eyebrow="Partnership philosophy" title="Aligned, honest, long-term." /></div>
          <div className="space-y-8 lg:col-span-7">
            <Reveal className="lead">A good venture partnership is a shared commitment, not a transaction. We look for opportunities where our product and engineering experience genuinely changes the odds — and where both sides are clear about what they contribute.</Reveal>
            <Reveal className="rounded-2xl border border-si-orange/30 bg-si-orange/5 p-6 text-base leading-relaxed text-[#ffd9b0]">
              Partnerships are selective and subject to evaluation. Submitting an application does not guarantee funding, equity arrangements, or a partnership. Any arrangement is negotiated individually.
            </Reveal>
          </div>
        </div>
      </section>
      <section className="border-t border-si-line bg-si-bg2 py-20 sm:py-28">
        <div className="wrap">
          <SectionHead eyebrow="Opportunities of interest" title="Who we'd love to meet." />
          <Stagger className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {INTERESTS.map((t) => <motion.p key={t} variants={itemVariants} className="panel p-6 text-lg font-semibold leading-snug">{t}</motion.p>)}
          </Stagger>
          <div className="mt-16 grid gap-6 lg:grid-cols-2">
            <Reveal className="panel p-8"><h3 className="mb-6 text-2xl font-bold">What SparkIdea can contribute</h3><BulletList items={WE_BRING} /></Reveal>
            <Reveal delay={0.1} className="panel p-8"><h3 className="mb-6 text-2xl font-bold">What founders are expected to contribute</h3><BulletList items={YOU_BRING} /></Reveal>
          </div>
        </div>
      </section>
      <section className="py-20 sm:py-28">
        <div className="wrap grid gap-12 lg:grid-cols-2">
          <div>
            <SectionHead eyebrow="Evaluation process" title="How applications are reviewed." />
            <ol className="mt-10 space-y-6">
              {EVAL.map(([t, d], i) => (
                <Reveal as="li" key={t} delay={i * 0.06} className="flex gap-5"><span className="grid h-10 w-10 shrink-0 place-items-center rounded-full border border-si-cyan font-mono text-xs text-si-cyan">0{i + 1}</span><span><span className="text-lg font-bold">{t}</span><span className="block text-base text-si-muted">{d}</span></span></Reveal>
              ))}
            </ol>
          </div>
          <div>
            <SectionHead eyebrow="Potential collaboration models" title="Ways we might work together." />
            <ul className="mt-10 divide-y divide-si-line border-y border-si-line">
              {PARTNERSHIP_TYPES.map((t) => <li key={t} className="py-4 text-lg font-semibold">{t}</li>)}
            </ul>
          </div>
        </div>
      </section>
      <section id="apply" className="scroll-mt-24 border-t border-si-line bg-si-bg2 py-20 sm:py-28">
        <div className="wrap grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <SectionHead eyebrow="Application" title="Pitch your idea." intro="Share enough for us to understand the opportunity. Please don't include confidential trade secrets — we can go deeper later under the right agreements." />
          </div>
          <div className="panel p-6 sm:p-10 lg:col-span-8"><PartnershipForm /></div>
        </div>
      </section>
      <section className="py-20 sm:py-28">
        <div className="wrap grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-4"><SectionHead eyebrow="FAQ" title="Partnership questions." /></div>
          <div className="lg:col-span-8"><FaqList items={FAQ_GROUPS[4].items} testId="vp-faq" /></div>
        </div>
      </section>
    </>
  );
}
